/**
 * The shape of a sign-in link — written down once, for both halves of the system.
 *
 * Authority: `docs/specs/um-app/09-auth-onboarding.md` §2 (SECURITY T3) and §3.3, and
 * `apps/web/src/pages/SignInPage.tsx`, which is the only thing that reads one.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHY THIS IS IN THE SHARED PACKAGE AND NOT IN EITHER APPLICATION
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * Two places compose a sign-in URL and one place parses it, and until now none of them
 * agreed by construction:
 *
 *   * `apps/api/src/start.ts` prints a development link for the fixture TD.
 *   * Tournament Configuration displays the real link for the TD to copy (D19/D20 — this
 *     application never sends the email itself).
 *   * `readSignInFragment()` in the web app reads whatever arrives.
 *
 * A composer and a parser that restate the same convention drift silently: the printed URL
 * still looks right, and the screen it lands on says `This Device Is Not Signed In`. So the
 * path, the parameter name and the composer live here, the parser is pinned against this
 * composer by a test in each half, and a change to the convention breaks a test rather than
 * a venue.
 *
 * **The token goes in the FRAGMENT, and that is the whole point.** A fragment is never put
 * in a request line, so it reaches no access log, no proxy log and no referrer header
 * (spec 09 §2, SECURITY T3). Nothing here ever puts a token in the path or the query.
 */
/** The one route a sign-in link points at. One application, one origin (stance D2). */
export const SIGN_IN_PATH = "/sign-in";
/** The fragment parameter that carries the token: `…/sign-in#token=<the token>`. */
export const SIGN_IN_TOKEN_PARAM = "token";
/**
 * The QUERY parameter that carries a deep link's intended destination (gap J9): an emailed
 * pack or alert link lands on sign-in when signed out and resumes the destination after
 * redemption.
 *
 * **It is a query parameter and not a fragment one, deliberately.** The fragment is reserved
 * for the secret, and it is struck from the address bar the instant it has been read; a
 * destination has to survive that. A path is not a secret — but it is also not trusted: what
 * may be resumed is decided against an allowlist of this application's own routes, in
 * `apps/web/src/app/resume.ts`, never here.
 */
export const SIGN_IN_NEXT_PARAM = "next";
/**
 * A sign-in link for `token`, at `origin` — the exact string the web app can read back.
 *
 * `origin` is a scheme and authority with no trailing slash (`https://td.example-hockey.hk`,
 * `http://localhost:5173`); a trailing slash is tolerated rather than doubled, because the
 * one thing worse than a strict helper is a link that silently becomes `//sign-in`.
 *
 * The token is percent-encoded. Nothing in this system issues a token needing it, and that
 * is precisely why it is done here rather than assumed away.
 */
export function signInLinkUrl(origin, token) {
    const base = origin.endsWith("/") ? origin.slice(0, -1) : origin;
    return `${base}${SIGN_IN_PATH}#${SIGN_IN_TOKEN_PARAM}=${encodeURIComponent(token)}`;
}
/**
 * The same link, carrying a destination to resume after redemption (gap J9).
 *
 * **The two halves go in different places and the order matters.** `next` is a QUERY
 * parameter, so it must sit before the `#`; the token stays in the fragment, because that is
 * what keeps it out of every log. A composer that put the query after the fragment would
 * produce a URL whose `next` is part of the fragment and is never read, and one that hunted
 * for the `#` in an already-composed string to splice a query in front of it — which is what
 * the boot message used to do — restates the convention a third time and can drift from it.
 *
 * `next` is percent-encoded here. It is not trusted here: what may actually be resumed is
 * decided against an allowlist of this application's own routes, in
 * `apps/web/src/app/resume.ts`, on arrival.
 */
export function signInLinkUrlWithNext(origin, token, next) {
    const base = origin.endsWith("/") ? origin.slice(0, -1) : origin;
    const query = `?${SIGN_IN_NEXT_PARAM}=${encodeURIComponent(next)}`;
    return `${base}${SIGN_IN_PATH}${query}#${SIGN_IN_TOKEN_PARAM}=${encodeURIComponent(token)}`;
}
//# sourceMappingURL=sign-in-link.js.map